import styled from 'styled-components';
import { ReactComponent as Slack } from '@elementary/common-ui/assets/select-slack.svg';
import { ReactComponent as Teams } from '@elementary/common-ui/assets/select-teams.svg';
import {
  Button,
  Chip,
  Flex,
  FlexColumn,
} from '@elementary/common-ui/components';
import { AlertIntegrationType } from '@elementary/common-ui/services/gql/types';
import { Tooltip, Typography } from '@mui/material';
import { MessagingIntegration } from 'shared/types';
import IntegrationDialog from '../../shared/Integration/IntegrationDialog';

const ProviderButton = styled(Button).attrs(() => ({
  variant: 'outlined',
}))`
  height: 150px;
  padding: 24px;
  flex: 1 0 0;
`;

type Props = {
  open: boolean;
  onClose: () => void;
  onSelected: (messagingIntegration: MessagingIntegration) => void;
  connectedTypes?: AlertIntegrationType[];
};

const IntegrationSelection = ({
  open,
  onClose,
  onSelected,
  connectedTypes = [],
}: Props) => {
  const showSlack = !connectedTypes.includes('SLACK');
  const showTeamsBot = !connectedTypes.includes('MICROSOFT_TEAMS_BOT');
  const showTeamsWebhook = !connectedTypes.includes('MICROSOFT_TEAMS');

  // The webhook is always a valid way to connect Teams; alongside the app we
  // mark it as deprecated and steer new setups to the app.
  const teamsWebhookButton = (
    <ProviderButton onClick={() => onSelected('Teams')}>
      <FlexColumn alignItems="center" gap={1}>
        <Teams />
        <Typography variant="caption" sx={{ color: 'common.gray2' }}>
          Webhook
        </Typography>
        <Chip label="Deprecated" color="default" size="small" />
      </FlexColumn>
    </ProviderButton>
  );

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title="Connect your messaging tool"
      hideSaveButton
      width={720}
    >
      <Flex gap={3} alignItems="stretch">
        {showSlack && (
          <ProviderButton onClick={() => onSelected('Slack')}>
            <Slack />
          </ProviderButton>
        )}

        {showTeamsBot && (
          <ProviderButton onClick={() => onSelected('TeamsBot')}>
            <FlexColumn alignItems="center" gap={1}>
              <Teams />
              <Typography variant="caption" sx={{ color: 'common.gray2' }}>
                App
              </Typography>
              <Chip label="Recommended" color="success" size="small" />
            </FlexColumn>
          </ProviderButton>
        )}

        {showTeamsWebhook && (
          <Tooltip title="The Microsoft Teams app is recommended for new setups.">
            {teamsWebhookButton}
          </Tooltip>
        )}
      </Flex>
    </IntegrationDialog>
  );
};

export default IntegrationSelection;
